import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

const DynamicForm = ({
  fields,
  initialValues = {}, // Default to an empty object
  onSubmit,
  onCancel,
  submitText = "Submit",
  cancelText = "Cancel"
}) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    // Use initialValues or an empty object as a safe fallback
    const safeInitialValues = initialValues || {};
    const initData = {};
    console.log("Initializing form with fields:", fields); // Debug log
    console.log("Initial values:", safeInitialValues); // Debug log
    
    fields.forEach(field => {
      let value = safeInitialValues[field.name];

      if (field.type === "date" && value) {
        value = new Date(value).toISOString().split("T")[0]; // Converts to 'YYYY-MM-DD'
      }

      if (field.type === "time-ampm" && value) {
        // Parse existing time format like "14:30 PM" or "2:30 PM"
        const timeParts = value.split(' ');
        if (timeParts.length === 2) {
          initData[`${field.name}_time`] = timeParts[0];
          initData[`${field.name}_ampm`] = timeParts[1];
        } else {
          initData[`${field.name}_time`] = '';
          initData[`${field.name}_ampm`] = 'AM';
        }
      }

      initData[field.name] =
        value !== undefined
          ? value
          : field.type === "checkbox"
          ? false
          : "";
      
      console.log(`Field ${field.name}:`, initData[field.name]); // Debug log
    });
    
    console.log("Final init data:", initData); // Debug log
    setFormData(initData);
  }, [fields, initialValues]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;
    console.log("Field Value:", fieldValue); // Debugging line
    setFormData(prev => ({ ...prev, [name]: fieldValue }));
  };

  const handleRichTextChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form Data Before Submit:", formData); // Debugging line
    console.log("Form Data Keys:", Object.keys(formData)); // Debug log
    console.log("Map field value:", formData.map); // Debug log
    onSubmit(formData);
  };

  return (
    <Form onSubmit={handleSubmit} className='row'>
      {fields.map(field => {
        switch (field.type) {
          case "text":
          case "email":
          case "password":
          case "date":
          case "number":
            return (
              <Form.Group key={field.name} controlId={`form-${field.name}`} className={`mb-3 ${field.colClass || 'col-md-6'}`}>
                <Form.Label>{field.label}</Form.Label>
                <Form.Control
                  type={field.type}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={formData[field.name]}
                    autoComplete={field.autoComplete || 'off'}
                  onChange={handleChange}
                  required={field.required}
                  rows={field.rows}
                />
              </Form.Group>
            );
          case "textarea":
            return (
              <Form.Group key={field.name} controlId={`form-${field.name}`} className={`mb-3 ${field.colClass || 'col-md-6'}`}>
                <Form.Label>{field.label}</Form.Label>
                <Form.Control
                  as="textarea"
                  name={field.name}
                  placeholder={field.placeholder}
                  value={formData[field.name]}
                  onChange={handleChange}
                  required={field.required}
                    autoComplete={field.autoComplete || 'off'}
                  rows={field.rows || 3}
                />
              </Form.Group>
            );
          case "richtext":
            return (
              <RichTextEditor
                key={field.name}
                field={field}
                value={formData[field.name] || ''}
                onChange={(value) => handleRichTextChange(field.name, value)}
              />
            );
          case "select":
            return (
              <Form.Group key={field.name} controlId={`form-${field.name}`} className={`mb-3 ${field.colClass || 'col-md-6'}`}>
                <Form.Label>{field.label}</Form.Label>
                <Form.Select
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                    autoComplete={field.autoComplete || 'off'}
                  required={field.required}
                >
                  {field.options?.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            );
          case "checkbox":
            return (
              <Form.Group key={field.name} controlId={`form-${field.name}`} className={`mb-3 ${field.colClass || 'col-md-6'}`}>
                <Form.Check
                  type="checkbox"
                  name={field.name}
                  label={field.label}
                    autoComplete={field.autoComplete || 'off'}
                  checked={!!formData[field.name]}
                  onChange={handleChange}
                />
              </Form.Group>
            );
            case "radio":
              return (
                <Form.Group key={field.name} controlId={`form-${field.name}`} className={`mb-3 ${field.colClass || 'col-md-6'}`}>
                  <Form.Label>{field.label}</Form.Label>
                  <div className='d-flex gap-2'>
                  {field.options?.map(option => (
                    <Form.Check
                      key={option.value}
                      type="radio"
                      name={field.name}
                      label={option.label}
                      value={option.value}
                        autoComplete={field.autoComplete || 'off'}
                      checked={formData[field.name] === option.value}
                      onChange={handleChange}
                      className='pet-radio'
                    />
                  ))}
                  </div>
                 
                </Form.Group>
              );
            case "time":
              return (
                <Form.Group key={field.name} controlId={`form-${field.name}`} className={`mb-3 ${field.colClass || 'col-md-6'}`}>
                  <Form.Label>{field.label}</Form.Label>
                  <Form.Control
                    type="time"
                    name={field.name}
                    placeholder={field.placeholder}
                      autoComplete={field.autoComplete || 'off'}
                    value={formData[field.name]}
                    onChange={handleChange}
                  />
                </Form.Group>
              );
            case "time-ampm":
              return (
                <Form.Group key={field.name} controlId={`form-${field.name}`} className={`mb-3 ${field.colClass || 'col-md-6'}`}>
                  <Form.Label>{field.label}</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="time"
                      name={`${field.name}_time`}
                      placeholder={field.placeholder}
                        autoComplete={field.autoComplete || 'off'}
                      value={formData[`${field.name}_time`] || ''}
                      onChange={(e) => {
                        const timeValue = e.target.value;
                        const ampmValue = formData[`${field.name}_ampm`] || 'AM';
                        const fullTime = timeValue ? `${timeValue} ${ampmValue}` : '';
                        handleChange({
                          target: {
                            name: field.name,
                            value: fullTime
                          }
                        });
                        handleChange(e);
                      }}
                      style={{ flex: 1 }}
                    />
                    <Form.Select
                      name={`${field.name}_ampm`}
                      value={formData[`${field.name}_ampm`] || 'AM'}
                        autoComplete={field.autoComplete || 'off'}
                      onChange={(e) => {
                        const ampmValue = e.target.value;
                        const timeValue = formData[`${field.name}_time`] || '';
                        const fullTime = timeValue ? `${timeValue} ${ampmValue}` : '';
                        handleChange({
                          target: {
                            name: field.name,
                            value: fullTime
                          }
                        });
                        handleChange(e);
                      }}
                      style={{ width: '80px' }}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </Form.Select>
                  </div>
                </Form.Group>
              );
          default:
            return null;
        }
      })}
      <div className='d-flex justify-content-end mt-4'>
          {onCancel && (
        <Button variant="secondary" onClick={onCancel} className="me-3">
          {cancelText}
        </Button>
      )}
         <Button variant="primary" type="submit" >
        {submitText}
      </Button>
      </div>
     
    </Form>
  );
};

// Rich Text Editor Component using Tiptap
const RichTextEditor = ({ field, value, onChange }) => {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'form-control',
        style: `min-height: ${field.rows ? field.rows * 20 : 200}px; padding: 12px; border: 1px solid #ced4da; border-radius: 0.375rem;`,
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <Form.Group controlId={`form-${field.name}`} className={`mb-3 ${field.colClass || 'col-md-6'}`}>
      <Form.Label>{field.label}</Form.Label>
      <div className="rich-text-editor-wrapper" style={{ border: '1px solid #ced4da', borderRadius: '0.375rem', backgroundColor: '#fff' }}>
        <div className="rich-text-toolbar" style={{ 
          borderBottom: '1px solid #ced4da', 
          padding: '8px', 
          display: 'flex', 
          gap: '4px',
          flexWrap: 'wrap',
          backgroundColor: '#f8f9fa'
        }}>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`btn btn-sm ${editor.isActive('bold') ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ border: 'none', padding: '4px 8px' }}
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`btn btn-sm ${editor.isActive('italic') ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ border: 'none', padding: '4px 8px' }}
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`btn btn-sm ${editor.isActive('strike') ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ border: 'none', padding: '4px 8px' }}
          >
            <s>S</s>
          </button>
          <div style={{ width: '1px', backgroundColor: '#ced4da', margin: '0 4px' }}></div>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`btn btn-sm ${editor.isActive('heading', { level: 1 }) ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ border: 'none', padding: '4px 8px' }}
          >
            H1
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`btn btn-sm ${editor.isActive('heading', { level: 2 }) ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ border: 'none', padding: '4px 8px' }}
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`btn btn-sm ${editor.isActive('heading', { level: 3 }) ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ border: 'none', padding: '4px 8px' }}
          >
            H3
          </button>
          <div style={{ width: '1px', backgroundColor: '#ced4da', margin: '0 4px' }}></div>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`btn btn-sm ${editor.isActive('bulletList') ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ border: 'none', padding: '4px 8px' }}
          >
            •
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`btn btn-sm ${editor.isActive('orderedList') ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ border: 'none', padding: '4px 8px' }}
          >
            1.
          </button>
          <div style={{ width: '1px', backgroundColor: '#ced4da', margin: '0 4px' }}></div>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`btn btn-sm ${editor.isActive('blockquote') ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ border: 'none', padding: '4px 8px' }}
          >
            "
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            className="btn btn-sm btn-outline-secondary"
            style={{ border: 'none', padding: '4px 8px' }}
          >
            ─
          </button>
          <div style={{ width: '1px', backgroundColor: '#ced4da', margin: '0 4px' }}></div>
          <button
            type="button"
            onClick={() => editor.chain().focus().undo().run()}
            className="btn btn-sm btn-outline-secondary"
            style={{ border: 'none', padding: '4px 8px' }}
          >
            ↶
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().redo().run()}
            className="btn btn-sm btn-outline-secondary"
            style={{ border: 'none', padding: '4px 8px' }}
          >
            ↷
          </button>
        </div>
        <div style={{ padding: '12px', minHeight: `${field.rows ? field.rows * 20 : 200}px` }}>
          <EditorContent editor={editor} />
        </div>
      </div>
      {field.required && !value && (
        <div className="text-danger small mt-1">This field is required</div>
      )}
      <style>{`
        .rich-text-editor-wrapper .ProseMirror {
          outline: none;
          min-height: ${field.rows ? field.rows * 20 : 200}px;
        }
        .rich-text-editor-wrapper .ProseMirror p {
          margin-bottom: 0.5rem;
        }
        .rich-text-editor-wrapper .ProseMirror p:last-child {
          margin-bottom: 0;
        }
        .rich-text-editor-wrapper .ProseMirror ul,
        .rich-text-editor-wrapper .ProseMirror ol {
          padding-left: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .rich-text-editor-wrapper .ProseMirror h1,
        .rich-text-editor-wrapper .ProseMirror h2,
        .rich-text-editor-wrapper .ProseMirror h3 {
          font-weight: bold;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
        }
        .rich-text-editor-wrapper .ProseMirror h1 {
          font-size: 2rem;
        }
        .rich-text-editor-wrapper .ProseMirror h2 {
          font-size: 1.5rem;
        }
        .rich-text-editor-wrapper .ProseMirror h3 {
          font-size: 1.25rem;
        }
        .rich-text-editor-wrapper .ProseMirror blockquote {
          border-left: 3px solid #ced4da;
          padding-left: 1rem;
          margin-left: 0;
          margin-right: 0;
          font-style: italic;
        }
      `}</style>
    </Form.Group>
  );
};

export default DynamicForm;
